import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../api/axiosInstance';
import { useAuth } from '../../contexts/AuthContext';
import { Textarea, Button } from '../ui';
import { FormField } from '../ui/Form';
import { DocumentTextIcon, HashtagIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Resultados from './Resultados';
import { buildFeGeneradaBody, parsePedidoIds } from './facturaGeneradaPayload';

interface Resultado {
  orderId: string;
  error?: string;
}

interface FeGeneradaResultItem {
  orderId?: number | string;
  error?: string;
}

interface FeGeneradaResponse {
  message?: string;
  results?: {
    success?: FeGeneradaResultItem[];
    failed?: FeGeneradaResultItem[];
  };
}

const FacturaGenerada: React.FC = () => {
  const [orderIds, setOrderIds] = useState<string>('');
  const [exitosos, setExitosos] = useState<string[]>([]);
  const [fallidos, setFallidos] = useState<Resultado[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { ids, invalid } = parsePedidoIds(orderIds);

    if (invalid.length > 0) {
      toast.error('Solo se aceptan IDs numéricos de pedido', { position: 'top-center' });
      return;
    }

    if (ids.length === 0) {
      toast.error('Por favor ingrese al menos un ID de pedido', { position: 'top-center' });
      return;
    }

    if (user?.id == null) {
      toast.error('No se pudo identificar el usuario de la sesión', { position: 'top-center' });
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = buildFeGeneradaBody(user.id, ids);
      const response = await axiosInstance.patch<FeGeneradaResponse>('/status/fe-generada', requestBody);
      const data = response.data;

      const successItems = data.results?.success;
      const exitososTransformados = Array.isArray(successItems)
        ? successItems.map(item => String(item.orderId ?? ''))
        : [];

      const failedItems = data.results?.failed;
      const fallidosTransformados = Array.isArray(failedItems)
        ? failedItems.map(item => ({
            orderId: String(item.orderId ?? ''),
            error: String(item.error || 'Error desconocido')
          }))
        : [];

      setExitosos(exitososTransformados);
      setFallidos(fallidosTransformados);

      if (fallidosTransformados.length > 0) {
        toast.error('Algunos pedidos no se pudieron actualizar', { position: 'top-center' });
      } else if (exitososTransformados.length > 0) {
        toast.success('Facturas marcadas correctamente', { position: 'top-center' });
      } else {
        toast.error(data.message || 'No se actualizó ningún pedido', { position: 'top-center' });
      }

      setOrderIds('');
    } catch (error) {
      let message = 'Error al marcar las facturas generadas';
      if (axios.isAxiosError(error)) {
        const apiMessage = (error.response?.data as { message?: string } | undefined)?.message;
        if (apiMessage) {
          message = apiMessage;
        }
      }
      toast.error(message, { position: 'top-center' });
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <DocumentTextIcon className="h-7 w-7 text-teal-600 mr-3" />
            <h2>factura generadas</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">
            Marca la factura como generada en pedidos que ya están en estado enviado. Solo usa el ID del pedido.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField
              label="IDs de pedidos"
              helpText="Separe múltiples IDs con comas o saltos de línea. El pedido debe estar en estado enviado."
              icon={HashtagIcon}
            >
              <Textarea
                value={orderIds}
                onChange={(e) => setOrderIds(e.target.value)}
                rows={8}
                placeholder={'10452\n10453\n10454'}
                aria-label="IDs de pedidos"
              />
            </FormField>

            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              leftIcon={CheckCircleIcon}
              fullWidth
              size="lg"
            >
              {isLoading ? 'Actualizando...' : 'Marcar facturas generadas'}
            </Button>
          </form>
        </div>

        <Resultados exitosos={exitosos} fallidos={fallidos} />
      </div>
    </div>
  );
};

export default FacturaGenerada;
